import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { useEffect, useRef } from "react";
import { CommonQuestionErrorComp } from "../components/ui";
import { useThemeContext } from "../globalContext/global-context";
import type {
  HostControlBindings,
  QuestionArrangement,
  QuestionInput,
  QuestionPrompt,
  QuestionSpace,
} from "./contracts";

export type QuestionHostFrameProps = {
  attemptId: number;
  answered?: boolean;
  submittable?: boolean;
  busy: boolean;
  arrangement: QuestionArrangement;
  controls: HostControlBindings;
  prompt: QuestionPrompt;
  promptInHistory: boolean;
  history?: React.ReactNode;
  primaryActionOwner: "host" | "view";
  input: QuestionInput;
  space: QuestionSpace;
  response: React.ReactNode;
  showChoiceHint?: boolean;
  autoResponse?: { disabled: boolean; run: () => void };
  autoSubmit?: { ready: boolean; run: () => void };
  runSuggestedAction?: () => boolean;
  choiceHint?: "multichoice" | "multiselect";
  inputHint?: "multitext";
};

export type QuestionGroupHostFrameProps = {
  attemptId: number;
  heading: string;
  children: React.ReactNode;
  controls: HostControlBindings;
  containerType?: "question_group" | "grid_question_group";
};

export type QuestionGroupHost = {
  Frame: React.ComponentType<QuestionGroupHostFrameProps>;
  ItemFrame: React.ComponentType<QuestionHostFrameProps>;
};

export const QuestionAnnouncement = ({
  prompt,
}: {
  prompt: QuestionPrompt;
}) => (
  <Box
    aria-live="polite"
    aria-atomic="true"
    sx={{
      position: "absolute",
      width: 1,
      height: 1,
      overflow: "hidden",
      clipPath: "inset(50%)",
    }}
  >
    {prompt.content}
  </Box>
);

export const HostControls = ({
  controls,
  primaryActionTestId,
  autoFocus = true,
}: {
  controls: HostControlBindings;
  primaryActionTestId?: string;
  autoFocus?: boolean;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus, controls.textInput?.fieldId]);
  if (
    !controls.textInput &&
    !controls.primaryAction &&
    !controls.secondaryAction
  )
    return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${theme.tokens.spacing["2"]}px`,
        width: "100%",
      }}
    >
      {controls.textInput && (
        <TextField
          inputRef={inputRef}
          id={controls.textInput.fieldId}
          label={controls.textInput.label}
          value={controls.textInput.value}
          disabled={controls.textInput.disabled}
          multiline={controls.textInput.multiline}
          placeholder={controls.textInput.hint}
          onChange={(event) => controls.textInput?.onChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              (!controls.textInput?.multiline || !event.shiftKey) &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              controls.textInput?.onConfirm();
            }
          }}
          error={Boolean(controls.textInput.value && controls.textInput.error)}
          helperText={
            controls.textInput.value && controls.textInput.error
              ? controls.textInput.error
              : controls.textInput.hint
          }
          slotProps={{
            input: {
              startAdornment: controls.textInput.prefix ? (
                <InputAdornment position="start">
                  {controls.textInput.prefix}
                </InputAdornment>
              ) : undefined,
              endAdornment: controls.textInput.suffix ? (
                <InputAdornment position="end">
                  {controls.textInput.suffix}
                </InputAdornment>
              ) : undefined,
            },
            htmlInput: {
              "data-testid": "text-input-field",
              "aria-describedby": `${controls.textInput.fieldId}-feedback`,
              inputMode: controls.textInput.inputMode,
            },
            formHelperText: { id: `${controls.textInput.fieldId}-feedback` },
          }}
          fullWidth
        />
      )}
      {(controls.secondaryAction || controls.primaryAction) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: `${theme.tokens.spacing["2"]}px`,
          }}
        >
          {controls.secondaryAction && (
            <Button
              data-testid="secondary-answer-action"
              type="button"
              variant={
                controls.secondaryAction.selected ? "contained" : "outlined"
              }
              aria-pressed={controls.secondaryAction.selected}
              disabled={controls.secondaryAction.disabled}
              onClick={controls.secondaryAction.run}
            >
              {controls.secondaryAction.label}
            </Button>
          )}
          {controls.primaryAction && (
            <Button
              data-testid={primaryActionTestId}
              type="button"
              variant="contained"
              disabled={controls.primaryAction.disabled}
              aria-busy={controls.primaryAction.pending}
              onClick={controls.primaryAction.run}
            >
              {controls.primaryAction.label}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export const PromptContent = ({ prompt }: { prompt: QuestionPrompt }) => {
  const { globalObject } = useThemeContext();
  return (
    <CommonQuestionErrorComp
      questionText={prompt.content}
      errorText={prompt.error}
      image={prompt.image}
      alt_text={prompt.altText}
      errorSx={{
        color:
          globalObject.themeData.normalizedTheme.tokens.color.feedback.error,
      }}
    />
  );
};

export const AnswerContent = ({
  responseContent,
  controls,
  primaryActionOwner,
  input,
  hidePrimaryAction = false,
  primaryActionTestId,
  renderControls,
}: Pick<QuestionHostFrameProps, "controls" | "primaryActionOwner" | "input"> & {
  responseContent: React.ReactNode;
  hidePrimaryAction?: boolean;
  primaryActionTestId?: string;
  renderControls?: (controls: HostControlBindings) => React.ReactNode;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${theme.tokens.spacing["2"]}px`,
        width: "100%",
      }}
    >
      {responseContent}
      {(
        renderControls ??
        ((bindings) => (
          <HostControls
            controls={bindings}
            primaryActionTestId={primaryActionTestId}
          />
        ))
      )({
        ...controls,
        ...((primaryActionOwner === "view" || hidePrimaryAction) && {
          primaryAction: undefined,
        }),
        ...(input !== "host-text" && { textInput: undefined }),
      })}
    </Box>
  );
};
